import { Injectable } from '@angular/core';
import { ModelRiskDynamicFieldFilterInput } from 'app/API.service';
import { CUSTOMAPIService } from 'app/custom-api.service';
import {
  RiskDynamicField,
  RiskDynamicFieldValues,
  buildRiskDynamicFieldValueId,
  hasRiskDynamicFieldValue,
  toRiskDynamicField,
  toRiskDynamicFieldInput,
  toRiskDynamicFieldValueInput,
  toRiskDynamicFieldValues,
} from 'app/risk-register/risk-register.constant';

/** How "delete field" should treat values already entered on existing risks. */
export enum RiskDynamicFieldDeleteMode {
  /** Remove the definition and every VALUE row entered against it. */
  ALL_RISKS = 'ALL_RISKS',
  /** Soft delete: hide from new risks, existing risks keep field and values. */
  NEW_RISKS_ONLY = 'NEW_RISKS_ONLY',
}

@Injectable({ providedIn: 'root' })
export class RiskDynamicFieldService {
  /**
   * Definitions cached per root entity. Holds the *promise* so concurrent
   * callers during the first load share one request rather than racing.
   */
  private cache = new Map<string, Promise<RiskDynamicField[]>>();

  constructor(private customApi: CUSTOMAPIService) { }

  // ---------------------------------------------------------------------------
  // Definitions
  // ---------------------------------------------------------------------------

  /**
   * Every definition for the entity, archived ones included, in authored order.
   *
   * Returns `[]` rather than throwing when the read fails: dynamic fields are
   * additive and a failed lookup must not block risk creation.
   */
  getFields(entityId: string, forceRefresh = false): Promise<RiskDynamicField[]> {
    if (!entityId) {
      return Promise.resolve([]);
    }
    if (forceRefresh) {
      this.cache.delete(entityId);
    }
    if (!this.cache.has(entityId)) {
      // Cached before awaiting so a second caller joins this request; dropped
      // again on failure so the next attempt retries instead of caching [].
      const request = this.fetchFields(entityId).catch(e => {
        // Still resolves to [] below, but logged so a failed read is not
        // indistinguishable from an entity with no fields.
        console.error('RiskDynamicFieldService: failed to load dynamic fields', e);
        this.cache.delete(entityId);
        throw e;
      });
      this.cache.set(entityId, request);
    }
    return this.cache.get(entityId).catch(() => []);
  }

  async getActiveFields(entityId: string, forceRefresh = false): Promise<RiskDynamicField[]> {
    const fields = await this.getFields(entityId, forceRefresh);
    return fields.filter(field => !field.isArchived);
  }

  async getFieldsForRisk(entityId: string, values: RiskDynamicFieldValues): Promise<RiskDynamicField[]> {
    const fields = await this.getFields(entityId);
    return fields.filter(field => !field.isArchived || hasRiskDynamicFieldValue(values?.[field.key]));
  }

  async createField(field: RiskDynamicField, entityId: string): Promise<void> {
    await this.customApi.CreateRiskDynamicField(toRiskDynamicFieldInput(field, entityId));
    this.invalidate(entityId);
  }

  /**
   * Updates a definition. `field.key` is the row id, so the label can change
   * freely without orphaning values stored under that key.
   */
  async updateField(field: RiskDynamicField, entityId: string): Promise<void> {
    const input = toRiskDynamicFieldInput(field, entityId);
    await this.customApi.UpdateRiskDynamicField({ ...input, id: field.key });
    this.invalidate(entityId);
  }

  async deleteField(field: RiskDynamicField, entityId: string, mode: RiskDynamicFieldDeleteMode): Promise<void> {
    if (mode === RiskDynamicFieldDeleteMode.NEW_RISKS_ONLY) {
      await this.updateField({ ...field, isArchived: true }, entityId);
      return;
    }
    await this.customApi.DeleteRiskDynamicField({ id: field.key });
    this.invalidate(entityId);
    // Deliberately not awaited.
    this.deleteValuesByParent(field.key, entityId);
  }

  private deleteValuesByParent(parentId: string, entityId?: string): void {
    this.fetchValueRowsByParent(parentId, entityId)
      .then(rows => Promise.all(rows.map(row => this.customApi.DeleteRiskDynamicField({ id: row.id }))))
      .catch(error => {
        console.error(`Error - deleteValuesByParent(${parentId}):`, error);
      });
  }

  clearValues(fieldId: string, entityId?: string): void {
    if (!fieldId) {
      return;
    }
    this.deleteValuesByParent(fieldId, entityId);
  }

  /** How many risks have answered this field — one VALUE row per risk. */
  async getUsageCount(fieldId: string, entityId?: string): Promise<number> {
    if (!fieldId) {
      return 0;
    }
    try {
      const rows = await this.fetchValueRowsByParent(fieldId, entityId);
      return rows.length;
    } catch {
      return 0;
    }
  }

  invalidate(entityId?: string): void {
    if (entityId) {
      this.cache.delete(entityId);
      return;
    }
    this.cache.clear();
  }

  // ---------------------------------------------------------------------------
  // Values
  // ---------------------------------------------------------------------------

  async getValues(riskId: string, entityId?: string): Promise<RiskDynamicFieldValues> {
    if (!riskId) {
      return {};
    }
    const filter: ModelRiskDynamicFieldFilterInput = entityId ? { entityId: { eq: entityId } } : null;
    try {
      const rows = [];
      let nextToken: string = null;
      do {
        const page = await this.customApi.RiskDynamicFieldByRiskId(riskId, null, filter, 200, nextToken);
        rows.push(...(page?.items || []));
        nextToken = page?.nextToken;
      } while (nextToken);
      return toRiskDynamicFieldValues(rows);
    } catch {
      return {};
    }
  }

  async saveValues(riskId: string, values: RiskDynamicFieldValues, entityId?: string): Promise<void> {
    if (!riskId || !values) {
      return;
    }
    for (const fieldId of Object.keys(values)) {
      const value = values[fieldId];
      const id = buildRiskDynamicFieldValueId(riskId, fieldId);
      if (!hasRiskDynamicFieldValue(value)) {
        // Cleared: drop the row so "no row" keeps meaning "unanswered".
        // Tolerated failure — the row may never have existed.
        try {
          await this.customApi.DeleteRiskDynamicField({ id });
        } catch {
          /* nothing stored for this field */
        }
        continue;
      }
      const input = toRiskDynamicFieldValueInput(riskId, fieldId, value, entityId);
      try {
        await this.customApi.UpdateRiskDynamicField({ ...input, id });
      } catch {
        await this.customApi.CreateRiskDynamicField(input);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Reads
  // ---------------------------------------------------------------------------
  /**
   * Definitions for the entity. `order` lives in the `metadata` blob so it
   * cannot be the index sort key — the ordering happens here.
   *
   * VALUE rows carry `entityId` too, so `byEntityId` returns both kinds. A
   * DEFINITION is the row with no `parentId`. DynamoDB filters after reading,
   * so this still walks every row the entity owns.
   */
  private async fetchFields(entityId: string): Promise<RiskDynamicField[]> {
    const rows = [];
    let nextToken: string = null;
    do {
      const page = await this.customApi.RiskDynamicFieldByEntityId(
        entityId,
        null,
        { parentId: { attributeExists: false } },
        200,
        nextToken
      );
      rows.push(...(page?.items || []));
      nextToken = page?.nextToken;
    } while (nextToken);

    return rows
      .filter(row => !!row && !row.parentId)
      .map(row => toRiskDynamicField(row))
      .sort((a, b) => a.order - b.order);
  }

  private async fetchValueRowsByParent(parentId: string, entityId?: string): Promise<{ id: string }[]> {
    const rows = [];
    let nextToken: string = null;
    const filter: ModelRiskDynamicFieldFilterInput = entityId ? { entityId: { eq: entityId } } : null;
    do {
      const page = await this.customApi.RiskDynamicFieldByParentId(parentId, null, filter, 200, nextToken);
      rows.push(...(page?.items || []));
      nextToken = page?.nextToken;
    } while (nextToken);
    return rows.filter(row => !!row);
  }
}
